import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { normaliseRole } from '../roles';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Search } from 'lucide-react';
import TalentBankReport from './TalentBankReport';
import { creditBalancesFromAggregate } from '../talentBank';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// Talent Bank, the staff screen: pick any student in the school and read their report.
//
// The family screen is MyTalentBank, a separate module -- not a role branch in here. Two reasons,
// and the second is the one that forced it. First, a school can switch the family module on for
// students without exposing this screen's class filter and roster search. Second, the queries are
// not the same query with a different id: a student or guardian must filter on the email field their
// security rule tests, or Firestore refuses the whole query rather than returning less. "Whose data
// may I ask for" is therefore a property of the screen, and the report below is what is genuinely
// shared -- it takes one student and knows nothing about who is looking.
const TalentBank = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile } = useOutletContext();

  // False for everyone on the first render after a reload, so the roster fetch has to wait for it --
  // firing early for a student would be denied, and the denial is indistinguishable from an empty
  // school.
  const ownRole = normaliseRole(userProfile?.role);
  const roleKnown = !!userProfile && !!ownRole;

  // --- Roster: who this screen can choose between ---
  const [studentList, setStudentList] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [selectedSid, setSelectedSid] = useState('');

  useEffect(() => {
    if (!activeSchoolId || !roleKnown) return;
    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.sid || !data.currentClass) return;
          list.push({
            id: d.id,
            sid: data.sid,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || t('talentBank.unknownName'),
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        setStudentList(list);
      } catch (e) {
        console.error('Error loading student list', e);
      }
      setLoadingStudents(false);
    };
    fetchStudents();
  }, [activeSchoolId, roleKnown]);

  const filteredStudents = useMemo(() => {
    const txt = searchText.toLowerCase();
    return studentList
      .filter(s => {
        const matchTxt = !txt || s.name.toLowerCase().includes(txt) || s.chineseName.includes(searchText) || s.sid.toLowerCase().includes(txt);
        const matchCls = !classFilter || s.currentClass === classFilter;
        return matchTxt && matchCls;
      })
      .sort((a, b) => a.currentClass.localeCompare(b.currentClass, undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0));
  }, [studentList, searchText, classFilter]);

  const classOptions = useMemo(() => [...new Set(studentList.map(s => s.currentClass))].sort(), [studentList]);

  const selectedMeta = useMemo(
    () => studentList.find(s => s.sid === selectedSid) || null,
    [studentList, selectedSid],
  );

  // --- OLE: staff read the activity document, which holds the whole roster ---
  const [oleActivities, setOleActivities] = useState([]);
  const [loadingOle, setLoadingOle] = useState(false);

  useEffect(() => {
    if (!activeSchoolId || !selectedSid) { setOleActivities([]); return; }
    setLoadingOle(true);
    getDocs(query(
      collection(db, 'schools', activeSchoolId, 'ole_record'),
      where('studentInvolved', 'array-contains', selectedSid),
    ))
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setOleActivities(list);
      })
      .catch(e => { console.error('Error loading OLE records', e); setOleActivities([]); })
      .finally(() => setLoadingOle(false));
  }, [activeSchoolId, selectedSid]);

  // --- Conduct records, and the aggregate the balances come from ---
  const [creditRecords, setCreditRecords] = useState([]);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [creditBalancesDoc, setCreditBalancesDoc] = useState({ yearly: {}, lifetime: {} });

  useEffect(() => {
    if (!activeSchoolId || !selectedSid) { setCreditRecords([]); return; }
    setLoadingCredit(true);
    getDocs(query(
      collection(db, 'schools', activeSchoolId, 'student_credit_record'),
      where('sid', '==', selectedSid),
    ))
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setCreditRecords(list);
      })
      .catch(e => { console.error('Error loading student credit records', e); setCreditRecords([]); })
      .finally(() => setLoadingCredit(false));
  }, [activeSchoolId, selectedSid]);

  // One document holds every student's balance, so it is fetched once per school rather than per
  // student -- and it is staff-only for the same reason, a rule cannot hand back one key of a map.
  useEffect(() => {
    if (!activeSchoolId) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_balances'))
      .then(snap => setCreditBalancesDoc(snap.exists() ? snap.data() : { yearly: {}, lifetime: {} }))
      .catch(e => console.error('Error loading student credit balances', e));
  }, [activeSchoolId]);

  const creditBalances = useMemo(
    () => creditBalancesFromAggregate(creditBalancesDoc, selectedSid),
    [creditBalancesDoc, selectedSid],
  );

  return (
    <TalentBankReport
      sid={selectedSid}
      studentMeta={selectedMeta}
      oleActivities={oleActivities}
      loadingOle={loadingOle}
      creditRecords={creditRecords}
      loadingCredit={loadingCredit}
      creditBalances={creditBalances}
      emptyState={(
        <>
          <Search size={64} style={{ opacity: 0.1, marginBottom: '1.5rem' }} />
          <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>{t('talentBank.selectStudentTitle')}</h3>
          <p>{t('talentBank.searchHint')}</p>
        </>
      )}
      pickerSlot={(
        <>
          <select className="input-field" value={classFilter} onChange={e => setClassFilter(e.target.value)} style={{ width: '100px', flexShrink: 0 }}>
            <option value="">{t('common.allClasses')}</option>
            {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select className="input-field" value={selectedSid} onChange={e => setSelectedSid(e.target.value)} style={{ width: '220px', flexShrink: 0 }}>
            <option value="">{t('common.selectStudent')}</option>
            {filteredStudents.map(s => (
              <option key={s.sid} value={s.sid}>
                {s.currentClass} ({s.classNumber}) - {s.name} {s.chineseName}
              </option>
            ))}
          </select>

          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Search size={14} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              className="input-field"
              aria-label={t('talentBank.searchStudents')}
              style={{ width: '160px', paddingLeft: '1.75rem' }}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>

          {loadingStudents && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{t('common.loading')}</span>
          )}
        </>
      )}
    />
  );
};

export default TalentBank;
